import {
  Box,
  Text,
  Button,
  IconButton,
  HStack,
  Switch,
  useDisclosure,
  Stack
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';

import { CloseIcon } from '@assets/icons';
import { convertRuleFormValuesToRuleset } from '@util/helpers';
import FocusRule from '@components/focus/focus-rule.component';
import FocusFormField from '@components/focus/focus-form-field.component';
import ControlledTooltip from '@components/general/controlled-tooltip.component';
import StyledModal from '@components/general/modals/styled-modal.component';
import StackRadio from '@components/general/form/radio.component';
import FocusRuleForm from './focus-rule-form.component';

const defaultValues = {
  titles: [{ value: '' }],
  domains: [{ invitees: 1, value: '' }],
  only_domains: [{ value: '' }],
  not_domains: [{ invitees: 1, value: '' }],
  never_domains: [{ value: '' }],
  emails: [{ value: '' }],
  organizers: [{ value: '' }]
};

export default function FocusEditRules({
  isAddToMeetingsChecked,
  handleAddToMeetingCheck,
  isSensitiveChecked,
  handleIsSensitiveCheck,
  rulesets,
  addRulesets,
  removeRuleset,
  visibility,
  handleVisibilityChange
}) {
  const disclosure = useDisclosure();
  const ruleForm = useForm({ defaultValues, reValidateMode: 'onBlur' });
  const visibilityForm = useForm({ values: { visibility }, reValidateMode: 'onBlur' });

  const addRule = (formValues) => {
    const newRulesets = convertRuleFormValuesToRuleset(formValues);
    addRulesets(newRulesets);
    ruleForm.reset(defaultValues);
    disclosure.onClose();
  };

  const visibilityOptions = [
    { value: 'Owner', label: 'Just Me' },
    { value: 'Collaborators', label: 'Anyone following this Focus' },
    { value: 'Tenant', label: 'Anyone at the company' }
  ];

  return (
    <>
      <FocusFormField label='Automation'>
        <HStack
          gap={3}
          align='flex-start'
        >
          <Switch
            mt={1}
            isChecked={rulesets?.length && isAddToMeetingsChecked}
            onChange={handleAddToMeetingCheck}
            disabled={rulesets?.length === 0}
          />
          <Box mb={0}>
            <Text
              fontWeight={600}
              mb={-1}
            >
              Automatically add Fora to all meetings matched to this category
            </Text>
            <Text
              as='i'
              fontSize='xs'
            >
              *Requires at least one automation rule
            </Text>
          </Box>
        </HStack>

        <HStack gap={3}>
          <Switch
            isChecked={isSensitiveChecked}
            onChange={handleIsSensitiveCheck}
          />
          <Text fontWeight={600}>
            Mark all meetings assigned to this category as{' '}
            <ControlledTooltip
              label={
                <Text>
                  <Text as='b'>Sensitive&nbsp;</Text>meetings will only generate summaries for Full
                  users that are invited to the meeting. Executive users will still receive all
                  summaries.
                </Text>
              }
            >
              {(tooltipProps) => (
                <Text
                  as='b'
                  textDecor='underline'
                  {...tooltipProps}
                >
                  sensitive
                </Text>
              )}
            </ControlledTooltip>
          </Text>
        </HStack>
      </FocusFormField>

      <FocusFormField label='Visibility'>
        <Stack gap={3}>
          <StackRadio
            name='visibility'
            width='100%'
            control={visibilityForm?.control}
            options={visibilityOptions}
          />
          <Button
            type='submit'
            form='visibility'
            variant='reskinWhite'
            onClick={visibilityForm.handleSubmit(({ visibility }) => {
              handleVisibilityChange(visibility);
            })}
          >
            Set Visibility
          </Button>
        </Stack>
      </FocusFormField>

      <FocusFormField label='Rules'>
        {rulesets?.length > 0 ? (
          rulesets.map((ruleset, i) => (
            <FocusRule
              key={`rule-${ruleset?.type}-${ruleset?.value}`}
              ruleset={ruleset}
            >
              <IconButton
                icon={<CloseIcon fontSize='18px' />}
                isRound
                variant='ghost'
                size='sm'
                onClick={() => removeRuleset(i, ruleset)}
              />
            </FocusRule>
          ))
        ) : (
          <Text fontSize='sm'>No Automation Rules Created</Text>
        )}
        <StyledModal
          disclosure={{
            ...disclosure,
            onOpen: () => {
              ruleForm.reset(defaultValues);
              disclosure.onOpen();
            }
          }}
          title='Create an automation rule'
          modalProps={{ scrollBehavior: 'inside' }}
          modalContentProps={{ minH: { base: '660px', '2xl': '95vh' } }}
          openButton={<Button variant='reskinWhite'>Add New Automation Rule</Button>}
          buttonText='Create Rule'
          buttonProps={{ type: 'submit', form: 'add-rules-form' }}
        >
          <Stack
            px={8}
            pb={8}
          >
            <FocusRuleForm form={ruleForm} />
            <Button
              type='submit'
              form='add-rules-form'
              variant='reskinPrimary'
              onClick={ruleForm.handleSubmit((values) => {
                ruleForm.reset(defaultValues);
                addRule(values);
              })}
            >
              Create Rule
            </Button>
          </Stack>
        </StyledModal>
      </FocusFormField>
    </>
  );
}
